import { useNavigate } from 'react-router-dom';
import { useUiStore } from '@/stores/ui';
import { makeT } from '@/lib/i18n';

/**
 * Lerala-pattern "Tsena" (Setswana: enter) sign-in badge.
 * Sticky top-right above the hero so customers don't scroll to find login.
 *
 * The word inside the badge cycles with the page's 5-second language
 * loop — Sign in / Tsena / Ngena / Shiga / Sign in / Pinda — so visitors
 * from every market see their language in the brand mark.
 */
export function TsenaBadge() {
  const navigate = useNavigate();
  const lang = useUiStore((s) => s.lang);
  const t = makeT(lang);
  const label = t('landing.signin');

  return (
    <button
      type="button"
      onClick={() => navigate('/login')}
      aria-label={`${label} — sign in to your account`}
      title={`${label} — sign in`}
      className="group fixed right-3 top-3 z-40 flex h-20 w-20 flex-col items-center justify-center gap-0.5 rounded-full border border-amber-200/60 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 shadow-lg shadow-amber-900/10 transition-all duration-200 hover:scale-105 hover:shadow-xl hover:shadow-amber-900/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 sm:h-28 sm:w-28 md:right-6 md:top-6 md:h-36 md:w-36 md:gap-1"
    >
      <Lerala className="h-9 w-9 text-amber-700 transition-transform duration-300 group-hover:rotate-12 sm:h-12 sm:w-12 md:h-16 md:w-16" />
      <span className="font-display text-[11px] font-semibold uppercase tracking-[0.10em] text-amber-900 sm:text-[14px] md:text-[20px]">
        {label}
      </span>
    </button>
  );
}

function Lerala({ className }: { className?: string }) {
  // 8-fold radial chevron rosette — the "lerala" weave
  // Stroke-only so it scales at any size without raster artefacts
  const ring = (radius: number, dash: string) => (
    <circle
      cx="32"
      cy="32"
      r={radius}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeDasharray={dash}
    />
  );
  const triangle = (rotate: number) => (
    <polygon
      key={rotate}
      points="32,8 36,18 28,18"
      transform={`rotate(${rotate} 32 32)`}
      fill="currentColor"
      opacity="0.85"
    />
  );
  const triSmall = (rotate: number) => (
    <polygon
      key={`s-${rotate}`}
      points="32,21 34,26 30,26"
      transform={`rotate(${rotate} 32 32)`}
      fill="currentColor"
      opacity="0.55"
    />
  );
  const angles = [0, 45, 90, 135, 180, 225, 270, 315];
  const offsetAngles = [22.5, 67.5, 112.5, 157.5, 202.5, 247.5, 292.5, 337.5];
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      {ring(28, '2 3')}
      {ring(20, '0')}
      {angles.map(triangle)}
      {offsetAngles.map(triSmall)}
      <circle cx="32" cy="32" r="4" fill="currentColor" />
      <circle cx="32" cy="32" r="2" fill="#FEF3C7" />
    </svg>
  );
}
